import { Observable, EMPTY, Subscription } from 'rxjs';
import ImageProcessingError from '@/model/ImageProcessingError';

type NestedKeyof<T> = { [K in keyof T]: keyof T[K] }[keyof T];
type Flatten<T> = {
  [K in NestedKeyof<T>]: {
    [P in keyof T]: K extends keyof T[P] ? T[P][K] : never
  }[keyof T]
};
interface ObservableList {
  [propName: string]: Observable<{}>;
}
type Extracted<T extends ObservableList> = {
  [K in keyof T]: T[K] extends Observable<infer R> ? R : never
};

export default function zipForUpload<L extends ObservableList, I extends object>(
  list: L,
  idExtractor: (arg: Extracted<L>[keyof L] | ImageProcessingError<unknown>) => I,
  errors: Observable<ImageProcessingError<unknown>>,
  discarded: Observable<I> = EMPTY,
): Observable<Flatten<Extracted<L>>> {
  return new Observable((observer) => {
    const buffer: Map<I, Partial<Extracted<L>>> = new Map();
    const subscriptions = new Subscription();
    const ignored = new WeakSet<I>();
    subscriptions.add(discarded.subscribe(id => { ignored.add(id); buffer.delete(id); }));
    Object.keys(list).forEach(<K extends keyof L>(key: K) => {
      subscriptions.add(list[key].subscribe((prop) => {
        const id = idExtractor(prop as Extracted<L>[K]);
        if (ignored.has(id)) return;
        buffer.set(id, {
          ...(buffer.get(id) as Partial<Extracted<L>> || {}),
          [key]: prop as Extracted<L>[K]
        });

        if (Object.keys(buffer.get(id) || {}).length === Object.keys(list).length) {
          const values = Object.values(buffer.get(id) || {}) as object[];
          buffer.delete(id);
          observer.next(
            values.reduce(
              (prev, current) => {
                return { ...prev, ...current };
              },
              {},
            ) as Flatten<Extracted<L>>,
          );
        }
      }));
    });
    subscriptions.add(errors.subscribe((err) => {
      const id = idExtractor(err);
      ignored.add(id);
      buffer.delete(id);
    }));
    return () => { subscriptions.unsubscribe(); buffer.clear(); };
  });
}
